import { useEffect, useState, useCallback, useRef } from 'react';
import { getStatus, startInstance, stopInstance, type StatusResult } from '@/api';
import { friendlyError, POLL_INTERVAL_MS, ACTION_TIMEOUT_MS, type AuthState } from '@/config';
import InstanceCard from '@/components/InstanceCard';
import ActionButtons from '@/components/ActionButtons';
import ControlBar from '@/components/ControlBar';
import ErrorBanner from '@/components/ErrorBanner';
import PanelLinkButton from '@/components/PanelLinkButton';

type PendingAction = 'start' | 'stop' | null;

interface Props {
  auth: AuthState;
  onLogout: () => void;
  onUnauthenticated: () => void;
}

export default function Dashboard({ auth, onLogout, onUnauthenticated }: Props) {
  const [status, setStatus] = useState<StatusResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const { ok, status: code, data } = await getStatus();
      if (code === 401) {
        onUnauthenticated();
        return null;
      }
      if (ok) {
        setStatus(data);
        return data;
      }
      setError(friendlyError(data.errorCode, data.error));
      return null;
    } catch {
      setError('Could not reach the backend.');
      return null;
    }
  }, [onUnauthenticated]);

  const initialLoad = useCallback(async () => {
    setLoading(true);
    await fetchStatus();
    setLoading(false);
  }, [fetchStatus]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  // Poll until the instance reaches the post-action target state.
  useEffect(() => {
    if (pending === null) {
      clearPoll();
      return;
    }
    const target = pending === 'start' ? 'running' : 'stopped';
    const startedAt = Date.now();
    let stopped = false;

    const tick = async () => {
      const data = await fetchStatus();
      if (data && data.state === target) {
        setPending(null);
        return;
      }
      if (!stopped && Date.now() - startedAt > ACTION_TIMEOUT_MS) {
        setPending(null);
        setError(`The instance did not reach the ${target} state in time. Check the AWS console.`);
      }
    };

    void tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearPoll();
    };
  }, [pending, fetchStatus]);

  const doStart = useCallback(async () => {
    if (pending) return;
    setError(null);
    try {
      const { ok, data } = await startInstance(auth.csrfToken);
      if (!ok) {
        setError(friendlyError(data.errorCode, data.error));
      } else {
        setPending('start');
        // The poll effect will refresh the real state (pending -> running).
      }
    } catch {
      setError('Could not reach the backend.');
    }
  }, [auth.csrfToken, pending]);

  const doStop = useCallback(async () => {
    if (pending) return;
    setError(null);
    try {
      const { ok, data } = await stopInstance(auth.csrfToken);
      if (!ok) {
        setError(friendlyError(data.errorCode, data.error));
      } else {
        setPending('stop');
      }
    } catch {
      setError('Could not reach the backend.');
    }
  }, [auth.csrfToken, pending]);

  const state = status?.state;
  const canStart = state === 'stopped' && auth.role === 'admin' && pending === null;
  const canStop = state === 'running' && auth.role === 'admin' && pending === null;

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="mx-auto max-w-3xl">
        <ControlBar user={auth.user} role={auth.role} onLogout={onLogout} onRefresh={fetchStatus} disabled={loading || !!pending} />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        {loading ? (
          <div className="mt-6 flex justify-center py-12">
            <div className="inline-block h-7 w-7 animate-spin rounded-full border-2 border-sky-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            <InstanceCard status={status} pending={pending} />
            <PanelLinkButton state={state ?? 'unknown'} pending={pending} csrfToken={auth.csrfToken} />
            <ActionButtons
              state={state ?? 'unknown'}
              canStart={canStart}
              canStop={canStop}
              pending={pending}
              onStart={doStart}
              onStop={doStop}
            />
          </>
        )}
        <footer className="mt-10 text-center text-xs text-slate-400">
          NallaSMP control panel — only the verified server instance can be modified.
        </footer>
      </div>
    </div>
  );
}
